import { createContext, ReactNode, useState } from 'react';
import { noop } from 'lodash-es';
import type {
  GetBatchStatus,
  SpuSelectMap,
  ToggleBatch,
  ToggleSku,
  ToggleSpu,
  CancelSelect,
} from '@knowledge/common/helpers/sku';

export const FilterBarValueContext = createContext({
  name: '',
  setName: noop,
  keyword: '',
  keywordType: 2,
  setKeyword: noop,
  setKeywordType: noop,
  storeScopes: [] as General.StoreScope[],
  setStoreScopes: noop,
  searchMode: false,
});

export const FilterBarValueProvider = ({ children }: { children: ReactNode }) => {
  const [keyword, setKeyword] = useState<string>('');
  const [keywordType, setKeywordType] = useState<number>(2);
  const [name, setName] = useState<string>('');
  const [storeScopes, setStoreScopes] = useState<General.StoreScope[]>([]);
  const searchMode = !!keyword || !!name || storeScopes.length > 0;

  return (
    <FilterBarValueContext.Provider
      value={{
        keyword,
        keywordType,
        setKeywordType,
        setKeyword,
        storeScopes,
        setStoreScopes,
        name,
        setName,
        searchMode,
      }}
    >
      {children}
    </FilterBarValueContext.Provider>
  );
};

export const OperateValueContext = createContext<{
  foldState: 'fold' | 'unfold';
  setFoldState: (state: 'fold' | 'unfold') => void;
  sortBy: '1' | '2';
  setSortBy: (sortBy: '1' | '2') => void;
}>({
  foldState: 'fold',
  setFoldState: noop,
  sortBy: '1',
  setSortBy: noop,
});

export const OperateValueProvider = ({
  children,
  level = 'SPU',
}: {
  children: ReactNode;
  level?: 'SKU' | 'SPU';
}) => {
  const [state, setState] = useState<'fold' | 'unfold'>(level === 'SKU' ? 'unfold' : 'fold');
  const [sortBy, setSortBy] = useState<'1' | '2'>('2');

  return (
    <OperateValueContext.Provider
      value={{ foldState: state, setFoldState: setState, sortBy, setSortBy }}
    >
      {children}
    </OperateValueContext.Provider>
  );
};

export const SpuSelectContext = createContext<{
  spuSelectMap: SpuSelectMap;
  toggleSku?: ToggleSku;
  toggleSpu?: ToggleSpu;
  toggleBatch?: ToggleBatch;
  getBatchStatus?: GetBatchStatus;
  cancelSelect?: CancelSelect;
}>({
  // state
  spuSelectMap: {} as SpuSelectMap,
  // mutations
  toggleSku: noop,
  toggleSpu: noop,
  toggleBatch: noop,
  getBatchStatus: noop as GetBatchStatus,
  cancelSelect: noop,
});
